import { createFormContext, zodResolver } from '@mantine/form'
import { useEffect } from 'react'

import { useMutationUpdateBookingProduct } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'
import { useNotifications } from '@flecto/ui'

import { useResetCustomValue } from '../../hooks/useResetCustomValue'
import { FormSectionPriceDeposit } from './FormSectionPriceDeposit'
import { FormSectionPriceRental } from './FormSectionPriceRental'
import { FormSectionQuantity } from './FormSectionQuantity'
import { useFormBookingProductSchema } from './useFormBookingProductSchema'

export const [FormProvider, useFormContext, useForm] =
  createFormContext<Partial<CoreBookingProduct>>()

type FormBookingProductProps = {
  id?: string
  bookingProduct?: CoreBookingProduct
  onSuccess?: () => void
  onChangeLoading?: (loading: boolean) => void
  setResetCustomValue: (value: boolean) => void
  setIsDirty: (value: boolean) => void
  resetCustomValue: boolean
  open?: boolean
}

export function FormBookingProduct({
  bookingProduct,
  onSuccess,
  onChangeLoading,
  setResetCustomValue,
  setIsDirty,
  resetCustomValue,
  open,
  ...props
}: FormBookingProductProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })

  const { showNotification } = useNotifications()

  const { schema, initialValues } = useFormBookingProductSchema({
    bookingProduct,
  })

  const { mutateAsync: updateBookingProduct } =
    useMutationUpdateBookingProduct()

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
  })

  const { handleResetCustomValue, handleResetPricingTier } =
    useResetCustomValue({
      form,
      setResetCustomValue,
      resetCustomValue,
    })

  async function saveBookingProduct() {
    try {
      onChangeLoading?.(true)

      await updateBookingProduct({
        bookingId: form.values.booking?.internal_id,
        product: form.values,
        productId: form.values.id,
      })

      showNotification({
        type: 'success',
        message: t('notification_update_booking_item_success'),
      })

      onSuccess?.()
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_update_booking_item_error'),
      })
    } finally {
      onChangeLoading?.(false)
    }
  }

  useEffect(() => {
    if (bookingProduct) {
      form.setValues(bookingProduct)
    } else {
      form.reset()
    }
  }, [bookingProduct])

  useEffect(() => {
    handleResetCustomValue()
  }, [resetCustomValue])

  useEffect(() => {
    setIsDirty(form.isDirty())
  }, [form.isDirty()])

  return (
    <FormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => saveBookingProduct())}>
        <FormSectionQuantity resetCustomValue={resetCustomValue} />
        <FormSectionPriceRental
          resetPricingTier={handleResetPricingTier}
          resetCustomValue={resetCustomValue}
          open={open}
        />
        <FormSectionPriceDeposit resetCustomValue={resetCustomValue} />
      </form>
    </FormProvider>
  )
}
